import { z } from "zod";

function addDuplicateQuestionIdIssue(
  ctx: z.RefinementCtx,
  index: number,
  id: string
) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `Duplicate question id "${id}" is not allowed.`,
    path: ["questions", index, "id"],
  });
}

function addDuplicateAnswerQuestionIdIssue(
  ctx: z.RefinementCtx,
  index: number,
  id: string
) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: `Duplicate answer question_id "${id}" is not allowed.`,
    path: ["answers", index, "question_id"],
  });
}

export const askUserQuestionOptionSchema = z
  .object({
    id: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    description: z.string().min(1).max(400).nullish(),
  })
  .strict();

export const askUserQuestionSchema = z
  .object({
    id: z.string().min(1).max(100),
    question: z.string().min(1).max(600),
    options: z.array(askUserQuestionOptionSchema).min(1).max(3).optional(),
    allow_other: z.boolean().default(true),
    placeholder: z.string().min(1).max(200).nullish(),
  })
  .strict()
  .refine(
    (value) => (value.options?.length ?? 0) > 0 || value.allow_other,
    {
      message: "At least one option or allow_other=true is required.",
      path: ["options"],
    }
  )
  .superRefine((value, ctx) => {
    const optionIds = new Set<string>();

    value.options?.forEach((option, index) => {
      if (optionIds.has(option.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate option id "${option.id}" is not allowed.`,
          path: ["options", index, "id"],
        });
        return;
      }

      optionIds.add(option.id);
    });
  });

export const askUserQuestionInputSchema = z
  .object({
    questions: z.array(askUserQuestionSchema).min(1).max(3),
    submit_label: z.string().min(1).max(40).nullish(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const questionIds = new Set<string>();

    value.questions.forEach((question, index) => {
      if (questionIds.has(question.id)) {
        addDuplicateQuestionIdIssue(ctx, index, question.id);
        return;
      }

      questionIds.add(question.id);
    });
  });

const askUserQuestionOptionAnswerSchema = z
  .object({
    question_id: z.string().min(1).max(100),
    question: z.string().min(1).max(600),
    response_type: z.literal("option"),
    answer_text: z.string().min(1).max(400),
    option_id: z.string().min(1).max(100),
    option_label: z.string().min(1).max(200),
  })
  .strict();

const askUserQuestionOtherAnswerSchema = z
  .object({
    question_id: z.string().min(1).max(100),
    question: z.string().min(1).max(600),
    response_type: z.literal("other"),
    answer_text: z.string().min(1).max(1000),
    option_id: z.string().min(1).max(100).nullish(),
    option_label: z.string().min(1).max(200).nullish(),
  })
  .strict();

export const askUserQuestionAnswerSchema = z.discriminatedUnion(
  "response_type",
  [askUserQuestionOptionAnswerSchema, askUserQuestionOtherAnswerSchema]
);

export const askUserQuestionApprovedResponseSchema = z
  .object({
    approved: z.literal(true),
    answers: z.array(askUserQuestionAnswerSchema).min(1).max(3),
  })
  .strict()
  .superRefine((value, ctx) => {
    const questionIds = new Set<string>();

    value.answers.forEach((answer, index) => {
      if (questionIds.has(answer.question_id)) {
        addDuplicateAnswerQuestionIdIssue(ctx, index, answer.question_id);
        return;
      }

      questionIds.add(answer.question_id);
    });
  });

export const askUserQuestionDeclinedResponseSchema = z
  .object({
    approved: z.literal(false),
  })
  .strict();

export const askUserQuestionResponseSchema = z.discriminatedUnion("approved", [
  askUserQuestionApprovedResponseSchema,
  askUserQuestionDeclinedResponseSchema,
]);

export type AskUserQuestionOption = z.infer<typeof askUserQuestionOptionSchema>;
export type AskUserQuestion = z.infer<typeof askUserQuestionSchema>;
export type AskUserQuestionAnswer = z.infer<typeof askUserQuestionAnswerSchema>;
export type AskUserQuestionInput = z.infer<typeof askUserQuestionInputSchema>;
export type AskUserQuestionApprovedResponse = z.infer<
  typeof askUserQuestionApprovedResponseSchema
>;
export type AskUserQuestionDeclinedResponse = z.infer<
  typeof askUserQuestionDeclinedResponseSchema
>;
export type AskUserQuestionResponse = z.infer<
  typeof askUserQuestionResponseSchema
>;
